// Learner preferences, shared by both courses (the Sanskrit course follows them through SanskritShell): Kids/Adults
// mode, explanation language, transliteration, text size, the pronunciation voice (the device voice, or Bhashini for
// Premium members) with its speed, and (editors, reviewers and admins only) whether review labels show.
// SSR-safe two-phase pattern: static defaults on the server and first render, then localStorage after mount.
// CSS-driven differences (sizes, hidden languages) are applied to <html> before paint by the script in _document.

import type { ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

export type Mode = 'kids' | 'adults'
export type Explain = 'hi' | 'en' | 'both'
export type VoiceEngine = 'device' | 'bhashini'
export type VoiceGender = 'female' | 'male'
export type MotionPref = 'system' | 'reduce' | 'full'

export interface Prefs {
    mode: Mode
    explain: Explain
    translit: boolean
    /** Show review labels on learner pages (editors, reviewers and admins only; this device only). */
    showReview: boolean
    /** Text size, 0.9 to 1.3 (this device only). */
    textScale: number
    /** Who reads words aloud where there is no teacher recording (this device only). */
    voice: VoiceEngine
    voiceGender: VoiceGender
    /** The device voice to use (a SpeechSynthesisVoice voiceURI), or null for the best Devanagari voice. */
    deviceVoice: string | null
    /** Reading speed, 0.5 to 1.2. */
    speechRate: number
    /** Animations: follow the device, fewer, or all (this device only). */
    motion: MotionPref
}

export const PREFS_KEY = 'vaani:prefs'

export const DEFAULT_PREFS: Prefs = {
    mode: 'adults',
    explain: 'both',
    translit: false,
    showReview: false,
    textScale: 1,
    voice: 'device',
    voiceGender: 'female',
    deviceVoice: null,
    speechRate: 0.85,
    motion: 'system'
}

interface PrefsContextValue extends Prefs {
    hydrated: boolean
    setMode: (mode: Mode) => void
    setExplain: (explain: Explain) => void
    setTranslit: (on: boolean) => void
    setShowReview: (on: boolean) => void
    setTextScale: (scale: number) => void
    setVoice: (voice: VoiceEngine) => void
    setVoiceGender: (gender: VoiceGender) => void
    setDeviceVoice: (voiceURI: string | null) => void
    setSpeechRate: (rate: number) => void
    setMotion: (motion: MotionPref) => void
    /** Apply preferences that came from the signed-in account. */
    adopt: (prefs: Partial<Prefs>) => void
}

const PrefsContext = createContext<PrefsContextValue | null>(null)

const applyToDocument = (prefs: Prefs) => {
    const root = document.documentElement

    root.setAttribute('data-mode', prefs.mode)
    root.style.setProperty('--text-scale', String(prefs.textScale))
    root.setAttribute('data-motion', prefs.motion)
    root.setAttribute('data-explain', prefs.explain)
    root.setAttribute('data-translit', prefs.translit ? 'on' : 'off')
}

const sanitize = (value: unknown): Prefs => {
    const raw = (value && typeof value === 'object' ? value : {}) as Partial<Prefs>

    return {
        mode: raw.mode === 'kids' ? 'kids' : 'adults',
        explain: raw.explain === 'hi' || raw.explain === 'en' ? raw.explain : 'both',
        translit: raw.translit === true,
        showReview: raw.showReview === true,
        textScale: typeof raw.textScale === 'number' && raw.textScale >= 0.9 && raw.textScale <= 1.3 ? raw.textScale : 1,
        voice: raw.voice === 'bhashini' ? 'bhashini' : 'device',
        voiceGender: raw.voiceGender === 'male' ? 'male' : 'female',
        deviceVoice: typeof raw.deviceVoice === 'string' && raw.deviceVoice.length < 300 ? raw.deviceVoice : null,
        speechRate: typeof raw.speechRate === 'number' && raw.speechRate >= 0.5 && raw.speechRate <= 1.2 ? raw.speechRate : 0.85,
        motion: raw.motion === 'reduce' || raw.motion === 'full' ? raw.motion : 'system'
    }
}

const syncToAccount = (prefs: Prefs) => {
    fetch('/api/me/prefs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs)
    }).catch(() => undefined)
}

export const PrefsProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [prefs, setPrefs] = useState<Prefs>(DEFAULT_PREFS)
    const [hydrated, setHydrated] = useState(false)

    useEffect(() => {
        try {
            const saved = localStorage.getItem(PREFS_KEY)

            if (saved) setPrefs(sanitize(JSON.parse(saved)))
        }
        catch {
            // Unreadable storage keeps the defaults.
        }

        setHydrated(true)
    }, [])

    const current = useRef<Prefs>(DEFAULT_PREFS)

    current.current = prefs

    const update = useCallback((patch: Partial<Prefs>, sync = true) => {
        const next = sanitize({ ...current.current, ...patch })

        current.current = next
        setPrefs(next)

        try {
            localStorage.setItem(PREFS_KEY, JSON.stringify(next))
        }
        catch {
            // Private mode: the choice still applies for this visit.
        }

        applyToDocument(next)

        if (sync) syncToAccount(next)
    }, [])

    const value = useMemo<PrefsContextValue>(() => ({
        ...prefs,
        hydrated,
        setMode: mode => update({ mode }),
        setExplain: explain => update({ explain }),
        setTranslit: translit => update({ translit }),
        setShowReview: showReview => update({ showReview }, false),
        setTextScale: textScale => update({ textScale }, false),
        setVoice: voice => update({ voice }, false),
        setVoiceGender: voiceGender => update({ voiceGender }, false),
        setDeviceVoice: deviceVoice => update({ deviceVoice }, false),
        setSpeechRate: speechRate => update({ speechRate }, false),
        setMotion: motion => update({ motion }, false),
        adopt: patch => update(patch, false)
    }), [prefs, hydrated, update])

    return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>
}

export const usePrefs = (): PrefsContextValue => {
    const ctx = useContext(PrefsContext)

    if (!ctx) throw new Error('usePrefs must be used inside PrefsProvider')

    return ctx
}

/** Inline script for _document: applies saved preferences to <html> before the first paint. */
export const PREFS_INIT_SCRIPT = `(function(){try{var d=document.documentElement;var p=JSON.parse(localStorage.getItem('${PREFS_KEY}')||'{}');if(/^\\/sanskrit(\\/|$)/.test(location.pathname))d.setAttribute('data-course','sanskrit');d.setAttribute('data-mode',p.mode==='kids'?'kids':'adults');d.setAttribute('data-explain',(p.explain==='hi'||p.explain==='en')?p.explain:'both');d.setAttribute('data-translit',p.translit===true?'on':'off');if(typeof p.textScale==='number'&&p.textScale>=0.9&&p.textScale<=1.3)d.style.setProperty('--text-scale',String(p.textScale));d.setAttribute('data-motion',p.motion==='reduce'||p.motion==='full'?p.motion:'system');}catch(e){}})();`
